/**
 * Compare Stations Page - PRO Feature
 * 
 * Compare how two radio stations perform with the same music format,
 * based on audience reaction, listener behavior and playlist efficiency.
 * 
 * SEO Structure:
 * H1: Compare Radio Stations Performance
 * H2: What is Compare Stations, Select Stations & Time Period, 
 *     Station Performance Summary, Common Songs Performance,
 *     Playlist Differentiation, Daypart Listener Reaction
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAccess } from '../contexts/AccessContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getBackendUrl } from '../utils/api';
import { 
  BarChart3, Radio, Calendar, Info, ChevronDown, ChevronUp,
  RefreshCw, AlertCircle, Music2, Clock, Sun, Sunset, Moon,
  TrendingUp, TrendingDown, Minus, ArrowRightLeft, Sparkles
} from 'lucide-react';
// v2.9.150 — Shazam badge integration
import { ShazamBadge } from '../components/StreamMonitor/ShazamBadge';
import { useShazamModeMap } from '../hooks/useShazamModeMap';
// v2.9.153 — Uniformity with People's Chart UI
import PreviewPlayButton from '../components/shared/PreviewPlayButton';

// SEO Configuration
const SEO_CONFIG = {
  title: 'Compare Radio Stations | Competitive Analysis Tool - Radio Solutions',
  description: 'Compare how radio stations perform with the same music format. Analyze audience reaction, listener retention, playlist efficiency and discover competitive advantages across Greek radio stations.',
  keywords: 'compare radio stations, radio station comparison, σύγκριση ραδιοφωνικών σταθμών, radio competitive analysis, ανταγωνιστική ανάλυση ραδιοφώνου, listener retention comparison, playlist efficiency metrics, greek radio analytics, radio station benchmarking, audience behavior analysis, radio performance metrics, station comparison tool, radio market analysis, broadcast competitive intelligence',
  ogUrl: 'https://radio-apps.online/compare-stations'
};

// Daypart icons
const DAYPART_ICONS = {
  morning: Sun,
  midday: Sun,
  afternoon: Sunset,
  drive: Sunset,
  night: Moon
};

// Tooltip component
const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg">
          {text}
        </div>
      )}
    </div>
  );
};

// Station selector component with pair filtering
const StationSelector = ({ 
  label, 
  stations, 
  selectedId, 
  onChange, 
  disabled, 
  otherSelectedId,
  assignedPairs = [],
  isStationB = false,
  stationAValue = null,
  t // Translation function passed as prop
}) => {
  // Filter stations based on assigned pairs if we're selecting station B
  // and the user has assigned pairs (not admin)
  const getAvailableStations = () => {
    // If no assigned pairs (admin), show all stations
    if (!assignedPairs || assignedPairs.length === 0) {
      return stations;
    }
    
    // For Station B, filter based on Station A selection
    if (isStationB && stationAValue) {
      const validStationBIds = new Set();
      assignedPairs.forEach(pair => {
        if (pair.station_a_id === stationAValue) {
          validStationBIds.add(pair.station_b_id);
        }
        if (pair.station_b_id === stationAValue) {
          validStationBIds.add(pair.station_a_id);
        }
      });
      return stations.filter(s => validStationBIds.has(s.id));
    }
    
    // For Station A, show all available stations from pairs
    return stations;
  };
  
  const availableStations = getAvailableStations();
  
  return (
    <div className="flex-1">
      <label className="block text-white/60 text-sm mb-2">{label}</label>
      <select
        value={selectedId || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || availableStations.length === 0}
        className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 disabled:opacity-50 [&>option]:bg-[#1A0730] [&>option]:text-white"
        data-testid={`station-selector-${label.toLowerCase().replace(' ', '-')}`}
      >
        <option value="" className="bg-[#1A0730] text-white">
          {availableStations.length === 0 ? `-- ${t('noStationsAvailable')} --` : `-- ${t('selectStation')} --`}
        </option>
        {availableStations.map((station) => (
          <option 
            key={station.id} 
            value={station.id}
            disabled={station.id === otherSelectedId}
            className="bg-[#1A0730] text-white disabled:text-white/40"
          >
            {station.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// Metric comparison card - compact with station names inside
const MetricCard = ({ label, valueA, valueB, stationAName, stationBName, unit = '', format = 'number', tooltip }) => {
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (format === 'percent') return `${val.toFixed(1)}%`;
    if (format === 'decimal') return val.toFixed(3);
    if (format === 'integer') return Math.round(val).toLocaleString();
    return val.toFixed(2);
  };

  const getDiff = () => {
    if (valueA === null || valueB === null) return null;
    return valueA - valueB;
  };

  const diff = getDiff();

  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
      <div className="flex items-center justify-center gap-2 mb-2">
        <span className="text-white/60 text-xs font-medium">{label}</span>
        {tooltip && (
          <Tooltip text={tooltip}>
            <Info className="w-3 h-3 text-white/40 cursor-help" />
          </Tooltip>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {/* Station A */}
        <div className="text-center bg-purple-500/10 rounded-md py-2 px-1">
          <p className="text-[10px] text-purple-300 truncate mb-1">{stationAName || 'Station A'}</p>
          <p className="text-base font-bold text-purple-400">{formatValue(valueA)}{unit}</p>
        </div>
        {/* Difference */}
        <div className="flex items-center justify-center">
          {diff !== null && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              diff > 0 ? 'bg-green-500/20 text-green-400' : 
              diff < 0 ? 'bg-red-500/20 text-red-400' : 
              'bg-gray-500/20 text-gray-400'
            }`}>
              {diff > 0 ? '+' : ''}{formatValue(diff)}
            </span>
          )}
        </div>
        {/* Station B */}
        <div className="text-center bg-teal-500/10 rounded-md py-2 px-1">
          <p className="text-[10px] text-teal-300 truncate mb-1">{stationBName || 'Station B'}</p>
          <p className="text-base font-bold text-teal-400">{formatValue(valueB)}{unit}</p>
        </div>
      </div>
    </div>
  );
};

// Common songs table - with clear visual separation between stations
const CommonSongsTable = ({ songs, stationAName, stationBName, t }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  if (!songs || songs.length === 0) {
    return (
      <div className="bg-white/5 rounded-lg p-8 text-center border border-white/10">
        <Music2 className="w-12 h-12 text-white/30 mx-auto mb-4" />
        <p className="text-white/60">{t('noCommonSongs')}</p>
        <p className="text-white/40 text-sm mt-2">{t('playlistsCompletelyDifferent')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="common-songs-table">
        <thead>
          <tr className="border-b border-white/20">
            <th className="text-left py-3 px-2 text-white/60 text-sm font-medium bg-white/5"></th>
            <th className="text-left py-3 px-4 text-white/60 text-sm font-medium bg-white/5">Τραγούδι</th>
            {/* Station A header - purple background */}
            <th className="text-center py-3 px-2 text-purple-300 text-sm font-bold bg-purple-500/20 border-l-2 border-purple-500" colSpan={4}>
              {stationAName}
            </th>
            {/* Station B header - teal background */}
            <th className="text-center py-3 px-2 text-teal-300 text-sm font-bold bg-teal-500/20 border-l-2 border-teal-500" colSpan={4}>
              {stationBName}
            </th>
          </tr>
          <tr className="border-b border-white/10 text-xs text-white/50">
            <th className="text-left py-2 px-2 bg-white/5"></th>
            <th className="text-left py-2 px-4 bg-white/5"></th>
            {/* Station A sub-headers */}
            <th className="text-center py-2 px-2 bg-purple-500/10 border-l-2 border-purple-500/50">Plays</th>
            <th className="text-center py-2 px-2 bg-purple-500/10">TLH</th>
            <th className="text-center py-2 px-2 bg-purple-500/10">Ret%</th>
            <th className="text-center py-2 px-2 bg-purple-500/10">Score</th>
            {/* Station B sub-headers */}
            <th className="text-center py-2 px-2 bg-teal-500/10 border-l-2 border-teal-500/50">Plays</th>
            <th className="text-center py-2 px-2 bg-teal-500/10">TLH</th>
            <th className="text-center py-2 px-2 bg-teal-500/10">Ret%</th>
            <th className="text-center py-2 px-2 bg-teal-500/10">Score</th>
          </tr>
        </thead>
        <tbody>
          {songs.map((song, idx) => (
            <React.Fragment key={song.canonical_key || idx}>
              <tr 
                className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
              >
                {/* v2.9.153 — Cover + 30s preview play button (matches People's Chart UI) */}
                <td className="py-3 px-2">
                  <PreviewPlayButton
                    src={song.cover_art_url}
                    previewUrl={song.preview_url}
                    trackKey={`compare-common-${song.canonical_key || idx}`}
                    artist={song.artist}
                    title={song.title}
                    size="sm"
                    testId={`compare-common-cover-${idx}`}
                  />
                </td>
                <td className="py-3 px-4 bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    {expandedRow === idx ? 
                      <ChevronUp className="w-4 h-4 text-white/40" /> : 
                      <ChevronDown className="w-4 h-4 text-white/40" />
                    }
                    <div>
                      <p className="text-white font-medium text-sm">{song.title}</p>
                      <p className="text-white/50 text-xs">{song.artist}</p>
                    </div>
                  </div>
                </td>
                {/* Station A metrics - purple tinted */}
                <td className="text-center py-3 px-2 text-purple-300 text-sm font-medium bg-purple-500/5 border-l-2 border-purple-500/30">{song.station_a.plays}</td>
                <td className="text-center py-3 px-2 text-purple-300 text-sm bg-purple-500/5">{song.station_a.tlh?.toFixed(2) ?? '—'}</td>
                <td className="text-center py-3 px-2 text-purple-300 text-sm bg-purple-500/5">{song.station_a.retention != null ? `${song.station_a.retention.toFixed(1)}%` : '—'}</td>
                <td className="text-center py-3 px-2 text-purple-300 text-sm bg-purple-500/5">{song.station_a.score != null ? song.station_a.score.toFixed(1) : '—'}</td>
                {/* Station B metrics - teal tinted */}
                <td className="text-center py-3 px-2 text-teal-300 text-sm font-medium bg-teal-500/5 border-l-2 border-teal-500/30">{song.station_b.plays}</td>
                <td className="text-center py-3 px-2 text-teal-300 text-sm bg-teal-500/5">{song.station_b.tlh?.toFixed(2) ?? '—'}</td>
                <td className="text-center py-3 px-2 text-teal-300 text-sm bg-teal-500/5">{song.station_b.retention != null ? `${song.station_b.retention.toFixed(1)}%` : '—'}</td>
                <td className="text-center py-3 px-2 text-teal-300 text-sm bg-teal-500/5">{song.station_b.score != null ? song.station_b.score.toFixed(1) : '—'}</td>
              </tr>
              {/* Expanded row - Daypart breakdown */}
              {expandedRow === idx && song.station_a.daypart_breakdown && (
                <tr className="bg-white/5">
                  <td colSpan={10} className="py-4 px-4">
                    <p className="text-white/60 text-xs mb-3 font-medium">Daypart Breakdown</p>
                    <div className="grid grid-cols-5 gap-4">
                      {Object.entries(song.station_a.daypart_breakdown).map(([dp, playsA]) => {
                        const playsB = song.station_b.daypart_breakdown?.[dp] || 0;
                        const DpIcon = DAYPART_ICONS[dp] || Sun;
                        return (
                          <div key={dp} className="bg-white/5 rounded-lg p-3 text-center">
                            <DpIcon className="w-4 h-4 mx-auto mb-1 text-white/40" />
                            <p className="text-xs text-white/60 capitalize mb-2">{dp}</p>
                            <div className="flex justify-center gap-4">
                              <span className="text-purple-400 text-sm">{playsA}</span>
                              <span className="text-white/30">vs</span>
                              <span className="text-teal-400 text-sm">{playsB}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Unique songs section
const UniqueSongsSection = ({ title, songs, stationName, color, t }) => {
  const [showAll, setShowAll] = useState(false);
  const displaySongs = showAll ? songs : songs?.slice(0, 10);

  if (!songs || songs.length === 0) {
    return (
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className={`font-medium mb-4 ${color === 'purple' ? 'text-purple-400' : 'text-teal-400'}`}>
          {title}
        </h4>
        <p className="text-white/40 text-sm">{t('noUniqueSongs')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 rounded-lg p-6 border border-white/10">
      <div className="flex items-center justify-between mb-4">
        <h4 className={`font-medium ${color === 'purple' ? 'text-purple-400' : 'text-teal-400'}`}>
          {title}
        </h4>
        <span className="text-white/40 text-sm">{songs.length} {t('songs')}</span>
      </div>
      <div className="space-y-2">
        {displaySongs.map((song, idx) => (
          <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* v2.9.153 — Cover + 30s preview, uniform with People's Chart */}
              <PreviewPlayButton
                src={song.cover_art_url}
                previewUrl={song.preview_url}
                trackKey={`compare-unique-${color}-${song.canonical_key || idx}`}
                artist={song.artist}
                title={song.title}
                size="sm"
                testId={`compare-unique-cover-${color}-${idx}`}
              />
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{song.title}</p>
                <p className="text-white/50 text-xs truncate">{song.artist}</p>
              </div>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              <span className={`text-sm ${color === 'purple' ? 'text-purple-300' : 'text-teal-300'}`}>
                {song.plays} plays
              </span>
              <span className="text-white/30 text-xs ml-2">
                ({song.retention != null ? `${song.retention.toFixed(1)}% ret` : '— ret'})
              </span>
            </div>
          </div>
        ))}
      </div>
      {songs.length > 10 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 text-sm text-purple-400 hover:text-purple-300"
        >
          {showAll ? 'Λιγότερα' : `Περισσότερα (${songs.length - 10})`}
        </button>
      )}
    </div>
  );
};

// Auto-insights section
const AutoInsights = ({ insights }) => {
  if (!insights || insights.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-purple-500/10 to-teal-500/10 border border-purple-500/20 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-purple-400" />
        <h3 className="text-white font-semibold">Auto-Insights</h3>
      </div>
      <div className="space-y-3">
        {insights.map((insight, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <div className={`w-2 h-2 mt-1.5 rounded-full ${
              insight.type === 'retention' ? 'bg-emerald-400' :
              insight.type === 'early_drop' ? 'bg-amber-400' :
              insight.type === 'efficiency' ? 'bg-blue-400' :
              'bg-purple-400'
            }`} />
            <p className="text-white/80 text-sm">{insight.text_el}</p>
          </div>
        ))}
      </div>
      <p className="text-white/40 text-xs mt-4 italic">
        Τα insights περιγράφουν παρατηρήσεις, χωρίς να δηλώνουν &quot;νικητή&quot;.
      </p>
    </div>
  );
};

// Daypart comparison section - with station labels inside each card
const DaypartComparison = ({ daypartData, stationAName, stationBName }) => {
  if (!daypartData) return null;

  const dayparts = ['morning', 'midday', 'afternoon', 'drive', 'night'];
  const labels = daypartData.daypart_labels || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      {dayparts.map((dp) => {
        const dataA = daypartData.station_a?.[dp];
        const dataB = daypartData.station_b?.[dp];
        const labelInfo = labels[dp] || {};
        const DpIcon = DAYPART_ICONS[dp] || Sun;

        return (
          <div key={dp} className="bg-white/5 rounded-lg p-4 border border-white/10">
            {/* Daypart header */}
            <div className="flex items-center gap-2 mb-2">
              <DpIcon className="w-4 h-4 text-white/40" />
              <span className="text-white/70 text-sm font-medium">{labelInfo.label_el || dp}</span>
            </div>
            <p className="text-white/30 text-xs mb-3">{labelInfo.time || ''}</p>
            
            {/* Station metrics with clear labels */}
            <div className="space-y-3">
              {/* Station A */}
              <div className="bg-purple-500/10 rounded-md p-2">
                <p className="text-[10px] text-purple-300 mb-1 truncate">{stationAName}</p>
                <div className="flex justify-between">
                  <span className="text-purple-400 text-sm font-medium">{dataA?.plays || 0} plays</span>
                  <span className="text-purple-300 text-xs">{dataA?.avg_retention != null ? `${dataA.avg_retention}% ret` : '— ret'}</span>
                </div>
              </div>
              {/* Station B */}
              <div className="bg-teal-500/10 rounded-md p-2">
                <p className="text-[10px] text-teal-300 mb-1 truncate">{stationBName}</p>
                <div className="flex justify-between">
                  <span className="text-teal-400 text-sm font-medium">{dataB?.plays || 0} plays</span>
                  <span className="text-teal-300 text-xs">{dataB?.avg_retention != null ? `${dataB.avg_retention}% ret` : '— ret'}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Main page component
const CompareStationsPage = ({ user }) => {
  const { hasFeatureAccess, access, loading: accessLoading } = useAccess();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const backendUrl = getBackendUrl();
  // v2.9.150 — Shazam mode lookup map (station_id → "mandatory" | "fallback")
  const { map: shazamModeMap } = useShazamModeMap(null);
  
  // State
  const [stations, setStations] = useState([]);
  const [timeWindows, setTimeWindows] = useState([]);
  const [stationA, setStationA] = useState(null);
  const [stationB, setStationB] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState('prev_week');
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [error, setError] = useState(null);
  const [formatWarning, setFormatWarning] = useState(null);
  
  // Compare mode: "stations" (1v1), "groups" (group vs group), "music" (type vs type)
  const [compareMode, setCompareMode] = useState('stations');
  const [groupA, setGroupA] = useState('');
  const [groupB, setGroupB] = useState('');
  const [musicTypeA, setMusicTypeA] = useState('');
  const [musicTypeB, setMusicTypeB] = useState('');
  const [groupComparisonData, setGroupComparisonData] = useState(null);
  
  // Access control state
  const [accessType, setAccessType] = useState(null); // 'admin', 'assigned_pairs', 'no_access'
  const [assignedPairs, setAssignedPairs] = useState([]);
  const [contactMessage, setContactMessage] = useState(null);
  
  // Access check
  // `hasFeatureAccess` returns true for admin/trial (AccessContext.jsx:225),
  // so the page mounts for trial users — but the backend still returns
  // `access_type: "no_access"` for them. We turn that into a demo preview.
  const hasAccess = hasFeatureAccess?.('compare_stations');
  const isTrialUser = access?.access_type === 'trial';

  // Redirect to pricing for users with no access at all (expired/none).
  // Trial users stay and see the demo preview.
  useEffect(() => {
    if (!accessLoading && !hasAccess && user && !isTrialUser) {
      navigate('/pricing');
    }
  }, [hasAccess, accessLoading, navigate, user, isTrialUser]);
  
  // Fetch initial data (stations and time windows)
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user?.user_id && !user?.id) return;
      
      setLoading(true);
      try {
        const userId = user.user_id || user.id;
        
        // Helper: Use XMLHttpRequest to avoid rrweb "Response body already used" errors
        const xhrGet = (url) => new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          const token = localStorage.getItem('token');
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.onload = function() {
            try {
              const parsed = JSON.parse(xhr.responseText);
              resolve({ status: xhr.status, data: parsed });
            } catch { resolve({ status: xhr.status, data: {} }); }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.timeout = 30000;
          xhr.send();
        });
        
        // Fetch stations and time windows in parallel
        const [stationsRes, windowsRes] = await Promise.all([
          xhrGet(`${backendUrl}/api/compare-stations/stations?user_id=${userId}`),
          xhrGet(`${backendUrl}/api/compare-stations/time-windows`)
        ]);
        
        if (stationsRes.status === 403) {
          navigate('/pricing');
          return;
        }
        
        const stationsData = stationsRes.data;
        const windowsData = windowsRes.data;
        
        // Set access control data
        setAccessType(stationsData.access_type || null);
        setAssignedPairs(stationsData.assigned_pairs || []);
        setContactMessage(stationsData.contact_message || null);
        
        setStations(stationsData.stations || []);
        setTimeWindows(windowsData.windows || []);
      } catch (err) {
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [user, backendUrl, navigate]);
  
  // Compare stations
  const handleCompare = useCallback(async () => {
    if (!stationA || !stationB || !selectedWindow) return;
    
    setComparing(true);
    setError(null);
    setComparisonData(null);
    
    try {
      const userId = user.user_id || user.id;
      const url = `${backendUrl}/api/compare-stations/compare?user_id=${userId}&station_a_id=${stationA}&station_b_id=${stationB}&time_window=${selectedWindow}`;
      
      // Use XMLHttpRequest to avoid "Response body already used" errors
      // caused by rrweb recorder intercepting fetch responses
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        const token = localStorage.getItem('token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        
        xhr.onload = function() {
          try {
            const parsed = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(parsed);
            } else {
              reject(new Error(parsed.detail || 'Failed to compare stations'));
            }
          } catch {
            reject(new Error('Invalid response from server'));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timed out'));
        };
        
        xhr.timeout = 60000;
        xhr.send();
      });
      
      setComparisonData(data);
      
      // Check for format warning
      const modeA = data.comparison?.station_a?.data_mode;
      const modeB = data.comparison?.station_b?.data_mode;
      if (modeA !== modeB) {
        setFormatWarning('Οι σταθμοί χρησιμοποιούν διαφορετική μέθοδο μέτρησης (Live Audience vs Stream Analytics). Τα metrics μπορεί να μην είναι άμεσα συγκρίσιμα.');
      } else {
        setFormatWarning(null);
      }
    } catch (err) {
      console.error('Compare error:', err);
      setError(err.message);
    } finally {
      setComparing(false);
    }
  }, [user, backendUrl, stationA, stationB, selectedWindow]);
  
  // Handle group/music type comparison
  const handleGroupCompare = useCallback(async () => {
    const isGroup = compareMode === 'groups';
    const valA = isGroup ? groupA : musicTypeA;
    const valB = isGroup ? groupB : musicTypeB;
    if (!valA || !valB || valA === valB) return;
    
    setComparing(true);
    setError(null);
    setGroupComparisonData(null);
    setComparisonData(null);
    
    try {
      const userId = user.user_id || user.id;
      const compareBy = isGroup ? 'station_group' : 'music_type';
      const tw = selectedWindow || '7d';
      const twMap = { 'prev_week': '7d', 'prev_2weeks': '14d', 'prev_month': '30d', 'today': '24h', 'yesterday': '24h', 'prev_48h': '48h' };
      const apiTw = twMap[tw] || '7d';
      const url = `${backendUrl}/api/compare-stations/compare-group?group_a=${valA}&group_b=${valB}&compare_by=${compareBy}&time_window=${apiTw}&user_id=${userId}`;
      
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        const token = localStorage.getItem('token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = function() {
          try {
            const parsed = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
            else reject(new Error(parsed.detail || 'Failed'));
          } catch { reject(new Error('Invalid response')); }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.timeout = 60000;
        xhr.send();
      });
      
      setGroupComparisonData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setComparing(false);
    }
  }, [user, backendUrl, compareMode, groupA, groupB, musicTypeA, musicTypeB, selectedWindow]);
  
  // Check if compare button should be enabled
  const canCompare = compareMode === 'stations'
    ? (stationA && stationB && stationA !== stationB && selectedWindow)
    : compareMode === 'groups'
      ? (groupA && groupB && groupA !== groupB)
      : (musicTypeA && musicTypeB && musicTypeA !== musicTypeB);
  
  if (accessLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A0730] via-[#2D0C4B] to-[#1A0730] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white/60">{t('loading')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0730] via-[#2D0C4B] to-[#1A0730] pb-20" data-testid="compare-stations-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SEO_CONFIG.title}</title>
        <meta name="description" content={SEO_CONFIG.description} />
        <meta name="keywords" content={SEO_CONFIG.keywords} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CONFIG.ogUrl} />
        <meta property="og:description" content={SEO_CONFIG.description} />
        <link rel="canonical" href={SEO_CONFIG.ogUrl} />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header with PRO badge */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-teal-600 rounded-xl">
              <ArrowRightLeft className="w-8 h-8 text-white" />
            </div>
            <div>
              {/* H1 - SEO */}
              <h1 className="text-3xl font-bold text-white">{t('compareStationsTitle')}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-bold">
                  PRO
                </span>
                <p className="text-white/60 text-sm">{t('compareStationsSubtitle')}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* H2: What is Compare Stations */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">{t('whatIsCompareStations')}</h2>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <p className="text-white/80 mb-3">
              {t('compareStationsDescP1')}
            </p>
            <p className="text-white/80">
              {t('compareStationsDescP2')}
            </p>
          </div>
        </section>
        
        {/* H2: Select Stations & Time Period */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">{t('selectStationsAndPeriod')}</h2>
          
          {/* No Access Message — differentiated for trial vs subscriber-without-access */}
          {accessType === 'no_access' && isTrialUser && (
            <div
              className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 border border-indigo-400/30 rounded-xl p-8 text-center"
              data-testid="compare-stations-demo-preview"
            >
              <Sparkles className="w-12 h-12 text-indigo-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">
                Demo Preview — Compare Stations
              </h3>
              <div className="max-w-xl mx-auto text-left space-y-3 text-white/75 text-sm mb-6">
                <p>
                  <span className="font-semibold text-indigo-300">Compare Stations</span> είναι το εργαλείο συγκριτικής ανάλυσης που σε βοηθά να δεις πώς τα πηγαίνει ο σταθμός σου σε σχέση με άλλους — στο ίδιο format, στον ίδιο νομό ή σε πανελλαδικό επίπεδο.
                </p>
                <p>
                  Παρέχει real-time σύγκριση σε 3 επίπεδα:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="text-white/90">Σταθμός vs Σταθμός</span> — απευθείας 1-on-1 σύγκριση</li>
                  <li><span className="text-white/90">Όμιλος vs Όμιλος</span> — για group managers</li>
                  <li><span className="text-white/90">Κατηγορία vs Κατηγορία</span> — Greek vs International κλπ</li>
                </ul>
                <p className="pt-2 text-white/60">
                  Η πλήρης πρόσβαση είναι διαθέσιμη με πακέτο <span className="font-semibold text-white/85">Playlister</span> ή <span className="font-semibold text-white/85">Ολοκληρωμένο</span>.
                </p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/90 hover:bg-white text-gray-900 font-semibold rounded-lg transition-colors"
                data-testid="compare-stations-upgrade-btn"
              >
                <Sparkles className="w-4 h-4" />
                Δες τα πακέτα
              </button>
            </div>
          )}

          {accessType === 'no_access' && !isTrialUser && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-8 text-center" data-testid="no-access-message">
              <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-amber-200 mb-3">
                {t('noAccessCompare')}
              </h3>
              <p className="text-amber-200/80 mb-4">
                {contactMessage || t('contactForAccess')}
              </p>
              <a 
                href="mailto:info@radiosolutionsgreece.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors"
              >
                <span>📧</span>
                {t('contact')}
              </a>
            </div>
          )}
          
          {/* Station Selection (only if has access) */}
          {accessType !== 'no_access' && (
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              {/* Compare Mode Tabs */}
              <div className="flex gap-2 mb-5 border-b border-white/10 pb-3" data-testid="compare-mode-tabs">
                {[
                  { mode: 'stations', label: t('csModeStation'), icon: '📻' },
                  { mode: 'groups', label: t('csModeGroup'), icon: '🏢' },
                  { mode: 'music', label: t('csModeCategory'), icon: '🎵' }
                ].map(tab => (
                  <button
                    key={tab.mode}
                    data-testid={`compare-mode-${tab.mode}`}
                    onClick={() => { setCompareMode(tab.mode); setError(null); setComparisonData(null); setGroupComparisonData(null); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      compareMode === tab.mode
                        ? 'bg-purple-600 text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
                    }`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
              {/* Free Grant Access Info */}
              {accessType === 'free_grant' && (
                <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-emerald-200 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Έχετε Δωρεάν Πρόσβαση σε όλους τους σταθμούς
                  </p>
                </div>
              )}
              
              {/* Assigned Pairs Info */}
              {accessType === 'assigned_pairs' && assignedPairs.length > 0 && (
                <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-purple-200 text-sm flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Έχετε πρόσβαση σε {assignedPairs.length} ζευγάρι{assignedPairs.length > 1 ? 'α' : ''} σταθμών
                  </p>
                </div>
              )}
              
              {/* 1v1 Station Selection */}
              {compareMode === 'stations' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Station A */}
                <StationSelector
                  label="Station A"
                  stations={stations}
                  selectedId={stationA}
                  onChange={(val) => {
                    setStationA(val);
                    if (assignedPairs.length > 0) {
                      setStationB(null);
                    }
                  }}
                  otherSelectedId={stationB}
                  assignedPairs={assignedPairs}
                  isStationB={false}
                  t={t}
                />
                
                {/* Station B */}
                <StationSelector
                  label="Station B"
                  stations={stations}
                  selectedId={stationB}
                  onChange={setStationB}
                  otherSelectedId={stationA}
                  assignedPairs={assignedPairs}
                  isStationB={true}
                  stationAValue={stationA}
                  t={t}
                />
                
                {/* Time Window */}
                <div className="flex-1">
                  <label className="block text-white/60 text-sm mb-2">{t('timePeriod')}</label>
                  <select
                    value={selectedWindow}
                    onChange={(e) => setSelectedWindow(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730] [&>option]:text-white"
                    data-testid="time-window-selector"
                  >
                    {timeWindows.map((window) => (
                      <option key={window.key} value={window.key} className="bg-[#1A0730] text-white">
                        {window[`label_${language}`] || window.label_el || window.label_en || window.key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              )}
              
              {/* Group vs Group Selection */}
              {compareMode === 'groups' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Όμιλος A</label>
                  <select
                    data-testid="group-a-selector"
                    value={groupA}
                    onChange={(e) => setGroupA(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730]"
                  >
                    <option value="">Επιλέξτε Όμιλο</option>
                    <option value="group_antenna">Group Antenna</option>
                    <option value="group_attikes">Group Αττικές Εκδόσεις</option>
                    <option value="group_audiomax">Group Audiomax</option>
                    <option value="group_frontstage">Group Frontstage</option>
                    <option value="group_kostaki">Group Κωστάκη</option>
                    <option value="group_politis">Group Politis</option>
                    <option value="group_skai">Group Skai</option>
                    <option value="group_independent">Group Ανεξάρτητοι</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Όμιλος B</label>
                  <select
                    data-testid="group-b-selector"
                    value={groupB}
                    onChange={(e) => setGroupB(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730]"
                  >
                    <option value="">Επιλέξτε Όμιλο</option>
                    <option value="group_antenna">Group Antenna</option>
                    <option value="group_attikes">Group Αττικές Εκδόσεις</option>
                    <option value="group_audiomax">Group Audiomax</option>
                    <option value="group_frontstage">Group Frontstage</option>
                    <option value="group_kostaki">Group Κωστάκη</option>
                    <option value="group_politis">Group Politis</option>
                    <option value="group_skai">Group Skai</option>
                    <option value="group_independent">Group Ανεξάρτητοι</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">{t('csTimePeriod')}</label>
                  <select
                    value={selectedWindow}
                    onChange={(e) => setSelectedWindow(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730]"
                  >
                    <option value="today">{t('today') || 'Σήμερα'}</option>
                    <option value="prev_48h">48h</option>
                    <option value="prev_week">{t('csPeriod7d')}</option>
                    <option value="prev_2weeks">{t('csPeriod14d')}</option>
                    <option value="prev_month">{t('csPeriod30d')}</option>
                  </select>
                </div>
              </div>
              )}
              
              {/* Music Type vs Music Type Selection */}
              {compareMode === 'music' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">{t('csCategoryA')}</label>
                  <select
                    data-testid="music-a-selector"
                    value={musicTypeA}
                    onChange={(e) => setMusicTypeA(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730]"
                  >
                    <option value="">{t('csSelectCategory')}</option>
                    <option value="greek">Greek</option>
                    <option value="international">International</option>
                    <option value="rock">Rock</option>
                    <option value="entechno">Entechno</option>
                    <option value="mixed">Mixed</option>
                    <option value="classic_hits">Classic Hits</option>
                    <option value="sophisticated">Sophisticated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">{t('csCategoryB')}</label>
                  <select
                    data-testid="music-b-selector"
                    value={musicTypeB}
                    onChange={(e) => setMusicTypeB(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730]"
                  >
                    <option value="">{t('csSelectCategory')}</option>
                    <option value="greek">Greek</option>
                    <option value="international">International</option>
                    <option value="rock">Rock</option>
                    <option value="entechno">Entechno</option>
                    <option value="mixed">Mixed</option>
                    <option value="classic_hits">Classic Hits</option>
                    <option value="sophisticated">Sophisticated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">{t('csTimePeriod')}</label>
                  <select
                    value={selectedWindow}
                    onChange={(e) => setSelectedWindow(e.target.value)}
                    className="w-full bg-[#1A0730] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 [&>option]:bg-[#1A0730]"
                  >
                    <option value="today">{t('today') || 'Σήμερα'}</option>
                    <option value="prev_48h">48h</option>
                    <option value="prev_week">{t('csPeriod7d')}</option>
                    <option value="prev_2weeks">{t('csPeriod14d')}</option>
                    <option value="prev_month">{t('csPeriod30d')}</option>
                  </select>
                </div>
              </div>
              )}
              
              {/* Format Warning */}
              {formatWarning && (
                <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-sm">{formatWarning}</p>
                </div>
              )}
            
              {/* Compare Button */}
              <button
                onClick={compareMode === 'stations' ? handleCompare : handleGroupCompare}
                disabled={!canCompare || comparing}
                className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="compare-button"
              >
                {comparing ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    {t('comparing')}
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-5 h-5" />
                    {t('compareButton')}
                  </>
                )}
              </button>
            
              {!canCompare && (
                <p className="text-white/40 text-sm mt-2">
                  {t('selectTwoStations')}
                </p>
              )}
            </div>
          )}
        </section>
        
        {/* Error display */}
        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-200">{error}</p>
          </div>
        )}
        
        {/* Group/Category Comparison Results */}
        {groupComparisonData && (
          <section className="mb-8" data-testid="group-comparison-results">
            <h2 className="text-xl font-semibold text-white mb-4">
              {groupComparisonData.group_a?.label || groupComparisonData.group_a?.name} vs {groupComparisonData.group_b?.label || groupComparisonData.group_b?.name}
            </h2>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[groupComparisonData.group_a, groupComparisonData.group_b].map((group, idx) => (
                <div key={idx} className={`bg-white/5 backdrop-blur-sm border rounded-xl p-5 ${idx === 0 ? 'border-purple-500/30' : 'border-teal-500/30'}`}>
                  <h3 className={`text-lg font-bold mb-3 ${idx === 0 ? 'text-purple-300' : 'text-teal-300'}`}>
                    {group?.label || group?.name}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-white/50">Σταθμοί:</span>
                      <span className="text-white ml-2 font-semibold">{group?.station_count}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Plays:</span>
                      <span className="text-white ml-2 font-semibold">{group?.total_plays?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Μοναδικά:</span>
                      <span className="text-white ml-2 font-semibold">{group?.unique_songs?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Μ.Ο. Ακροατών:</span>
                      <span className="text-white ml-2 font-semibold">{group?.avg_listeners}</span>
                    </div>
                  </div>
                  
                  {/* Station Breakdown */}
                  {group?.station_breakdown?.length > 0 && (
                    <div className="mt-4 border-t border-white/10 pt-3">
                      <h4 className="text-xs text-white/40 uppercase mb-2">Ανάλυση ανά Σταθμό</h4>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {group.station_breakdown.map((sb, i) => (
                          <div key={i} className="flex justify-between items-center text-xs">
                            <span className="flex items-center gap-1.5 text-white/70 truncate mr-2">
                              <span className="truncate">{sb.station_name}</span>
                              {shazamModeMap?.get?.(sb.station_id) && (
                                <ShazamBadge
                                  mode={shazamModeMap.get(sb.station_id)}
                                  size="sm"
                                />
                              )}
                            </span>
                            <div className="flex gap-3 text-white/50 flex-shrink-0">
                              <span>{sb.plays} plays</span>
                              <span>{sb.unique_songs} songs</span>
                              <span>{sb.avg_listeners} listeners</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Top Songs */}
                  {group?.top_songs?.length > 0 && (
                    <div className="mt-4 border-t border-white/10 pt-3">
                      <h4 className="text-xs text-white/40 uppercase mb-2">Top 10 Τραγούδια</h4>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {group.top_songs.slice(0, 10).map((song, i) => (
                          <div key={i} className="flex justify-between items-center text-xs">
                            <span className="text-white/70 truncate mr-2">
                              <span className="text-white/40 mr-1">{i + 1}.</span>
                              {song.artist} - {song.title}
                            </span>
                            <span className="text-white/50 flex-shrink-0">{song.plays}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Winner Summary */}
            {groupComparisonData.winner && (
              <div className="bg-gradient-to-r from-purple-500/10 to-teal-500/10 border border-white/10 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-3">Σύγκριση</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-white/50">Περισσότερα plays:</span>
                    <span className="text-emerald-400 font-semibold">
                      {(groupComparisonData.winner.more_plays === groupComparisonData.group_a?.name ? groupComparisonData.group_a?.label : groupComparisonData.group_b?.label) || groupComparisonData.winner.more_plays}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50">Μεγαλύτερη ποικιλία:</span>
                    <span className="text-blue-400 font-semibold">
                      {(groupComparisonData.winner.more_variety === groupComparisonData.group_a?.name ? groupComparisonData.group_a?.label : groupComparisonData.group_b?.label) || groupComparisonData.winner.more_variety}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50">Υψηλότερο κοινό:</span>
                    <span className="text-amber-400 font-semibold">
                      {(groupComparisonData.winner.higher_audience === groupComparisonData.group_a?.name ? groupComparisonData.group_a?.label : groupComparisonData.group_b?.label) || groupComparisonData.winner.higher_audience}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
        
        {/* Comparison Results */}
        {comparisonData && (
          <>
            {/* Auto-Insights */}
            {comparisonData.auto_insights && comparisonData.auto_insights.length > 0 && (
              <section className="mb-8">
                <AutoInsights insights={comparisonData.auto_insights} />
              </section>
            )}
            
            {/* H2: Station Performance Summary */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Station Performance Summary</h2>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                {/* Metrics grid - compact with station names inside each card */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <MetricCard
                    label="Avg Retention"
                    valueA={comparisonData.station_a_summary?.avg_retention}
                    valueB={comparisonData.station_b_summary?.avg_retention}
                    stationAName={comparisonData.station_a_summary?.station_name}
                    stationBName={comparisonData.station_b_summary?.station_name}
                    format="percent"
                    tooltip="Μέσο ποσοστό διατήρησης ακροατών"
                  />
                  <MetricCard
                    label="Avg Early Drop"
                    valueA={comparisonData.station_a_summary?.avg_early_drop}
                    valueB={comparisonData.station_b_summary?.avg_early_drop}
                    stationAName={comparisonData.station_a_summary?.station_name}
                    stationBName={comparisonData.station_b_summary?.station_name}
                    format="percent"
                    tooltip="Μέσο ποσοστό πρώιμης αποχώρησης"
                  />
                  <MetricCard
                    label="Playlist Efficiency"
                    valueA={comparisonData.station_a_summary?.playlist_efficiency}
                    valueB={comparisonData.station_b_summary?.playlist_efficiency}
                    stationAName={comparisonData.station_a_summary?.station_name}
                    stationBName={comparisonData.station_b_summary?.station_name}
                    format="decimal"
                    tooltip="TLH ανά play - απόδοση playlist"
                  />
                  <MetricCard
                    label="LSI Stability"
                    valueA={comparisonData.station_a_summary?.lsi_stability}
                    valueB={comparisonData.station_b_summary?.lsi_stability}
                    stationAName={comparisonData.station_a_summary?.station_name}
                    stationBName={comparisonData.station_b_summary?.station_name}
                    format="decimal"
                    tooltip="Δείκτης σταθερότητας ακροαματικότητας"
                  />
                  <MetricCard
                    label="Total Songs"
                    valueA={comparisonData.station_a_summary?.total_songs}
                    valueB={comparisonData.station_b_summary?.total_songs}
                    stationAName={comparisonData.station_a_summary?.station_name}
                    stationBName={comparisonData.station_b_summary?.station_name}
                    format="integer"
                    tooltip="Συνολικός αριθμός μοναδικών τραγουδιών"
                  />
                  <MetricCard
                    label="Total Plays"
                    valueA={comparisonData.station_a_summary?.total_plays}
                    valueB={comparisonData.station_b_summary?.total_plays}
                    stationAName={comparisonData.station_a_summary?.station_name}
                    stationBName={comparisonData.station_b_summary?.station_name}
                    format="integer"
                    tooltip="Συνολικός αριθμός μεταδόσεων (context only)"
                  />
                </div>
                
                <p className="text-white/30 text-xs mt-3 text-center">
                  {t('aggregateMetricsNote')}
                </p>
              </div>
            </section>
            
            {/* H2: Common Songs Performance */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Common Songs Performance</h2>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-white/60 text-sm">
                    Τραγούδια που παίχτηκαν και από τους δύο σταθμούς: {comparisonData.common_songs?.count || 0}
                  </p>
                  <Tooltip text={t('clickForDaypart')}>
                    <Info className="w-4 h-4 text-white/40 cursor-help" />
                  </Tooltip>
                </div>
                <CommonSongsTable 
                  songs={comparisonData.common_songs?.songs}
                  stationAName={comparisonData.station_a_summary?.station_name}
                  stationBName={comparisonData.station_b_summary?.station_name}
                  t={t}
                />
              </div>
            </section>
            
            {/* H2: Playlist Differentiation */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Playlist Differentiation (Unique Songs)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <UniqueSongsSection
                  title={`Μόνο στο ${comparisonData.station_a_summary?.station_name}`}
                  songs={comparisonData.unique_songs?.station_a?.songs}
                  stationName={comparisonData.station_a_summary?.station_name}
                  color="purple"
                  t={t}
                />
                <UniqueSongsSection
                  title={`Μόνο στο ${comparisonData.station_b_summary?.station_name}`}
                  songs={comparisonData.unique_songs?.station_b?.songs}
                  stationName={comparisonData.station_b_summary?.station_name}
                  color="teal"
                  t={t}
                />
              </div>
            </section>
            
            {/* H2: Daypart Listener Reaction */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Daypart Listener Reaction</h2>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <p className="text-white/60 text-sm mb-4">
                  {t('daypartComparisonNote')}
                </p>
                <DaypartComparison
                  daypartData={comparisonData.daypart_comparison}
                  stationAName={comparisonData.station_a_summary?.station_name}
                  stationBName={comparisonData.station_b_summary?.station_name}
                />
              </div>
            </section>
            
            {/* Differentiation note */}
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
              <p className="text-purple-200 text-sm">
                <strong>Σημείωση:</strong> Το Compare Stations δεν αντικαθιστά το Song Analyzer. 
                {t('stationComparisonDisclaimer')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompareStationsPage;
